import React from 'react';

import classNames from 'classnames';

import { formatMoneyWithLocale } from 'Helpers/formatService';

/**
 * TicketingImpactHeader
 *
 * The headline of the Commit Ticketing screen — the ticketing sibling of
 * UncommittedImpactHeader (Direct Commitment). A ticketing commitment is a total
 * amount, so every stat is a money figure:
 *   Uncommitted now  →  This commitment  →  Uncommitted after
 *
 * Ticketing is cash-only, so there is deliberately no VIK split here.
 *
 * Over-commitment is hard-blocked rather than warned about (a deliberate divergence
 * from Direct Commitment), so an "after" balance below zero is unreachable via the UI.
 * It is still rendered in red if it somehow occurs, so a bad state is visible rather
 * than silent.
 *
 * Props:
 *  - context:     { crumbs, line }  what is being committed against
 *  - now:         uncommitted balance before this commitment
 *  - committing:  the amount being committed now
 *  - blocked:     boolean           committing more than the balance allows
 */
export default function TicketingImpactHeader({ context = {}, now = 0, committing = 0, blocked = false }) {
    const after = now - committing;

    const Stat = ({ label, value, negative = false, positive = false, highlight = false }) => {
        const isNegative = negative && value < 0;

        return (
            <div
                className={classNames('tw-px-5 tw-py-4 tw-min-w-0', {
                    'tw-bg-green-25/40': highlight && !isNegative,
                    'tw-bg-red-25/40': highlight && isNegative,
                    'tw-bg-charcoal-10': !highlight,
                })}
            >
                <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">{label}</div>
                <div
                    className={classNames('tw-text-xl tw-font-bold tw-tabular-nums', {
                        'tw-text-red': isNegative,
                        'tw-text-green': positive && !isNegative,
                        'tw-text-charcoal': !isNegative && !positive,
                    })}
                >
                    {formatMoneyWithLocale(value)}
                </div>
            </div>
        );
    };

    return (
        <div
            className={classNames(
                // 2x2 below xl (a narrow viewport plus the sidebar cannot fit four tiles in a
                // row), 1x4 from xl up. min-w-0 on every tile lets the grid shrink with the card.
                // Hairlines come from a 1px gap over the container background — divide-x/y only
                // draws column dividers, which breaks down in the 2x2 arrangement.
                'tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-px tw-border tw-rounded-lg tw-overflow-hidden tw-bg-charcoal-15',
                blocked ? 'tw-border-red' : 'tw-border-charcoal-15',
            )}
        >
            <div className="tw-px-5 tw-py-4 tw-min-w-0 tw-bg-charcoal-10">
                {context.crumbs && <div className="tw-text-xs tw-text-charcoal-50 tw-font-medium tw-mb-1">{context.crumbs}</div>}
                <div className="tw-text-lg tw-font-bold tw-text-charcoal">{context.line || '-'}</div>
            </div>
            <Stat label={$t('Uncommitted now')} value={now} />
            {/* Positive and green: ticketing is income, so the amount being committed is money
                arriving. It used to be rendered as a red negative, borrowed from the expenditure
                screen, which read as a loss. The balance it consumes is the "after" tile's job. */}
            <Stat label={$t('This commitment')} value={committing} positive />
            <Stat label={$t('Uncommitted after')} value={after} negative highlight />
        </div>
    );
}
